package com.partejar.app.feature.home.data

interface HomeDataSource {
    suspend fun loadHome(): HomeDashboardData
}

sealed class HomeDataException(
    message: String,
    cause: Throwable? = null
) : Exception(message, cause)

class HomeSessionExpiredException : HomeDataException("Session expired")
class HomeWorkspaceNotConfiguredException : HomeDataException("Current workspace is not configured")
class HomeWorkspaceForbiddenException : HomeDataException("Current workspace is forbidden")
class HomePayloadException : HomeDataException("Home payload is invalid")
class HomeUnavailableException(cause: Throwable? = null) :
    HomeDataException("Home is unavailable", cause)

fun interface HomeDateTimeProvider {
    fun now(): HomeDateTime
}

class SystemHomeDateTimeProvider : HomeDateTimeProvider {
    override fun now(): HomeDateTime = currentHomeDateTime()
}

internal expect fun currentHomeDateTime(): HomeDateTime

data class HomeDateTime(
    val hour: Int,
    val dayOfWeek: Int,
    val dayOfMonth: Int,
    val month: Int,
    val year: Int = 1970
)

data class HomeDashboardData(
    val userName: String,
    val dateTime: HomeDateTime,
    val attentionItems: List<HomeAttentionData>,
    val upcomingPregnancies: List<HomePregnancyData>,
    val todayAppointments: List<HomeAppointmentData>
)

data class HomeAttentionData(
    val id: String,
    val summary: String,
    val quantity: Int = 1,
    val level: HomeAttentionLevel = HomeAttentionLevel.ATTENTION,
    val type: HomePendingType? = null,
    val relatedPatientIds: List<String> = emptyList()
)

enum class HomeAttentionLevel {
    INFO,
    ATTENTION,
    ERROR
}

enum class HomePendingType {
    WORKSHOP,
    FOLLOW_UP,
    SIGNATURE;

    companion object
}

enum class HomePregnancyOperationalStatus {
    ON_TRACK,
    ACTION_REQUIRED
}

data class HomePregnancyData(
    val id: String,
    val name: String,
    val babyName: String?,
    val gestationalWeeks: Int,
    val gestationalDays: Int,
    val dueDate: String,
    val responsible: String?,
    val daysUntilDueDate: Int,
    val actionRequired: String?,
    val operationalStatus: HomePregnancyOperationalStatus = HomePregnancyOperationalStatus.ON_TRACK,
    val pendingTypes: List<HomePendingType> = emptyList()
)

data class HomeAppointmentData(
    val id: String,
    val time: String,
    val minutesFromMidnight: Int,
    val type: String,
    val relatedPerson: String,
    val endTime: String? = null,
    val patientId: String? = null
)
